import type { EnemyKind, ResourceKind, ResourceNode, Vec2 } from "./types";
import { RESOURCES } from "./resources";

export type ExpeditionId = "verdant-ruins" | "ember-quarry";
export type BossId = "thorn-warden" | "ember-tortoise";
export interface ExpeditionDefinition {
  id: ExpeditionId;
  name: string;
  subtitle: string;
  description: string;
  objective: string;
  bossName: string;
  bossId: BossId;
  bossHint: string;
  reward: string;
  color: string;
  accent: string;
  difficulty: string;
  spawn: Vec2;
  bossPosition: Vec2;
  guards: EnemyKind[][];
  resources: Array<[ResourceKind, number, number]>;
}

export const EXPEDITIONS: Record<ExpeditionId, ExpeditionDefinition> = {
  "verdant-ruins": {
    id: "verdant-ruins",
    name: "苔光遗迹",
    subtitle: "一场会呼吸的林间远行",
    description:
      "穿过蕨林，收集药草与木材。修复两枚古老路标，唤醒沉睡的荆冠守望者。",
    objective: "修复林间路标，前往东端遗迹挑战领主",
    bossId: "thorn-warden",
    bossName: "荆冠守望者",
    bossHint: "突刺横向躲避；荆潮靠近核心；灵潮打断蓄力后集火。",
    reward: "灵露 ×5 · 浮土 ×6 · 铜叶 ×24 · 月露种 ×2",
    color: "#284c55",
    accent: "#b0e3bb",
    difficulty: "启程 · 建议 1–3 位伙伴",
    spawn: { x: -280, y: 485 },
    bossPosition: { x: 1740, y: 485 },
    guards: [
      ["mite", "mite", "mite"],
      ["mite", "moth"],
      ["spitter", "mite"],
    ],
    resources: [
      ["tree", -365, 350],
      ["herb", -125, 380],
      ["berry", -270, 610],
      ["tree", 80, 560],
      ["herb", 140, 390],
      ["tree", 530, 600],
      ["berry", 740, 570],
      ["herb", 930, 300],
      ["rock", 1040, 610],
      ["tree", 1460, 380],
      ["herb", 1860, 620],
      ["amber", 1970, 470],
    ],
  },
  "ember-quarry": {
    id: "ember-quarry",
    name: "赤晶荒原",
    subtitle: "追寻岩层深处的心跳",
    description:
      "沿着赤晶矿脉深入荒原。采集琥珀，击破玄甲守卫，解开矿坑深处的封印。",
    objective: "重燃矿脉路标，前往东端熔晶台挑战领主",
    bossId: "ember-tortoise",
    bossName: "赤晶玄甲",
    bossHint: "震荡外环危险、内圈安全；绕开吐息；收招时核心暴露。",
    reward: "灵露 ×7 · 浮土 ×8 · 铜叶 ×32 · 赤椒种 ×2",
    color: "#674651",
    accent: "#ffcc94",
    difficulty: "进阶 · 建议 3–4 位伙伴",
    spawn: { x: -280, y: 485 },
    bossPosition: { x: 1740, y: 485 },
    guards: [
      ["brute", "spitter", "mite"],
      ["sentinel", "mite"],
      ["spitter", "moth"],
    ],
    resources: [
      ["rock", -365, 350],
      ["berry", -270, 610],
      ["amber", -125, 380],
      ["rock", 80, 560],
      ["herb", 140, 390],
      ["rock", 530, 600],
      ["amber", 740, 570],
      ["herb", 930, 300],
      ["rock", 1040, 610],
      ["amber", 1460, 380],
      ["rock", 1860, 620],
      ["amber", 1970, 470],
    ],
  },
};

export interface ExpeditionObjective {
  id: string;
  name: string;
  pos: Vec2;
  complete: boolean;
  encounterId: string;
}
export interface ExpeditionEncounter {
  id: string;
  pos: Vec2;
  triggered: boolean;
  cleared: boolean;
  enemyIds: number[];
  kinds: EnemyKind[];
}
export interface ExpeditionRun {
  mapId: ExpeditionId;
  phase: "exploring" | "boss" | "cleared";
  objectives: ExpeditionObjective[];
  encounters: ExpeditionEncounter[];
  exit: Vec2;
  gathered: number;
  bossId: number | null;
}

export function createRun(id: ExpeditionId): ExpeditionRun {
  const map = EXPEDITIONS[id];
  return {
    mapId: id,
    phase: "exploring",
    exit: { x: -400, y: 485 },
    gathered: 0,
    bossId: null,
    objectives: [
      {
        id: "north-seal",
        name: id === "verdant-ruins" ? "苔光路标" : "赤晶路标",
        pos: { x: 650, y: 350 },
        complete: false,
        encounterId: "north",
      },
      {
        id: "south-seal",
        name: id === "verdant-ruins" ? "月泉路标" : "熔纹路标",
        pos: { x: 1000, y: 585 },
        complete: false,
        encounterId: "south",
      },
    ],
    encounters: [
      {
        id: "entrance",
        pos: { x: 110, y: 485 },
        triggered: false,
        cleared: false,
        enemyIds: [],
        kinds: map.guards[0],
      },
      {
        id: "north",
        pos: { x: 650, y: 350 },
        triggered: false,
        cleared: false,
        enemyIds: [],
        kinds: map.guards[1],
      },
      {
        id: "south",
        pos: { x: 1000, y: 585 },
        triggered: false,
        cleared: false,
        enemyIds: [],
        kinds: map.guards[2],
      },
    ],
  };
}

/** 当次远征资源使用独立 ID，既不污染家园冷却，也不能在原地无限再生。 */
export function expeditionResources(id: ExpeditionId): ResourceNode[] {
  return EXPEDITIONS[id].resources.map(([kind, x, y], index) => ({
    id: 10000 + index,
    kind,
    pos: { x, y },
    radius: 30,
    health: RESOURCES[kind].health,
    maxHealth: RESOURCES[kind].health,
    active: true,
    respawnTimer: 0,
    sway: index,
  }));
}
